import React, { useEffect, useMemo, useState } from "react";

/**
 * Vertical scroll progress ruler — compass style.
 * - Tall thin column of horizontal "tick" lines of varying widths
 * - Single red horizontal line (wider) marks the current scroll position
 * - White ticks above (passed) are slightly brighter than ticks below (yet to scroll)
 */
export const ScrollRuler = ({ count = 80 }) => {
    const [pct, setPct] = useState(0);

    useEffect(() => {
        const update = () => {
            const h = document.documentElement;
            const max = h.scrollHeight - h.clientHeight;
            setPct(max > 0 ? Math.min(1, Math.max(0, h.scrollTop / max)) : 0);
        };
        window.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);
        update();
        return () => {
            window.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, []);

    const widths = useMemo(() => {
        const arr = [];
        let seed = 19;
        const rand = () => {
            seed = (seed * 9301 + 49297) % 233280;
            return seed / 233280;
        };
        for (let i = 0; i < count; i++) {
            arr.push(8 + Math.floor(rand() * 16));
        }
        return arr;
    }, [count]);

    const activeIdx = Math.floor(pct * (count - 1));

    return (
        <div
            aria-hidden="true"
            data-testid="scroll-ruler"
            style={{
                position: "fixed",
                right: 22,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 96,
                display: "flex",
                flexDirection: "column",
                gap: 5,
                alignItems: "flex-end",
                pointerEvents: "none",
                mixBlendMode: "difference",
                maxHeight: "72vh",
                overflow: "hidden",
            }}
        >
            {widths.map((w, i) => {
                const isActive = i === activeIdx;
                const isPrev = i < activeIdx;
                return (
                    <span
                        key={i}
                        style={{
                            display: "block",
                            height: 1,
                            width: isActive ? 32 : w,
                            background: isActive
                                ? "#6f0a14"
                                : isPrev
                                ? "rgba(255,255,255,0.55)"
                                : "rgba(255,255,255,0.18)",
                            transition:
                                "width 0.25s cubic-bezier(0.65, 0, 0.35, 1), background 0.25s",
                            boxShadow: isActive ? "0 0 8px rgba(111, 10, 20, 0.5)" : undefined,
                        }}
                    />
                );
            })}
        </div>
    );
};

export default ScrollRuler;
